import React, { useState } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Loader2, Plus, Trash2, FileText, StickyNote, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useCaseNotes } from '@/hooks/useCaseNotes';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';

interface CaseNotesTabProps {
  caseId: string;
  contactIds?: string[];
}

export default function CaseNotesTab({ caseId, contactIds = [] }: CaseNotesTabProps) {
  const { t } = useTranslation('common');
  const { notes, loading, createNote, deleteNote, loadNotes } = useCaseNotes(caseId, contactIds);
  const [showForm, setShowForm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [newNoteTitle, setNewNoteTitle] = useState('');
  const [newNoteContent, setNewNoteContent] = useState('');

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) {
      toast.error(t('notesTab.noteEmpty'));
      return;
    }

    setProcessing(true);
    try {
      await createNote({
        title: newNoteTitle.trim() || undefined,
        content: newNoteContent.trim(),
      });
      toast.success(t('notesTab.noteAdded'));
      setNewNoteTitle('');
      setNewNoteContent('');
      setShowForm(false);
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error(t('notesTab.failedAdd'));
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm(t('notesTab.deleteConfirm'))) return;

    try {
      await deleteNote(noteId);
      toast.success(t('notesTab.noteDeleted'));
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error(t('notesTab.failedDelete'));
    }
  };

  const handleImportKommoNotes = async () => {
    if (!contactIds || contactIds.length === 0) {
      toast.info('Nenhum contato associado ao caso');
      return;
    }

    setIsImporting(true);
    let totalImported = 0;

    try {
      for (const contactId of contactIds) {
        const { data, error } = await supabase.functions.invoke('kommo-sync', {
          body: { action: 'import_kommo_notes', contact_id: contactId }
        });

        if (error) {
          console.error('Error importing notes for contact:', contactId, error);
          continue;
        }

        if (data?.notes_imported) {
          totalImported += data.notes_imported;
        }
      }

      if (totalImported > 0) {
        toast.success(t('notesTab.importSuccess', { count: totalImported }));
        // Reload notes to show imported ones
        loadNotes();
      } else {
        toast.info(t('notesTab.noNotesToImport'));
      }
    } catch (error) {
      console.error('Error importing Kommo notes:', error);
      toast.error(t('notesTab.importError'));
    } finally {
      setIsImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      {!showForm && (
        <div className="mb-6 flex gap-2">
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('notesTab.addNote')}
          </Button>
          {contactIds.length > 0 && (
            <Button
              variant="outline"
              onClick={handleImportKommoNotes}
              disabled={isImporting}
            >
              {isImporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('notesTab.importing')}
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  {t('notesTab.importFromKommo')}
                </>
              )}
            </Button>
          )}
        </div>
      )}

      {showForm && (
        <Card className="mb-6">
          <CardContent className="pt-6 space-y-3">
            <Input
              placeholder={t('notesTab.noteTitle')}
              value={newNoteTitle}
              onChange={(e) => setNewNoteTitle(e.target.value)}
              disabled={processing}
            />
            <Textarea
              placeholder={t('notesTab.enterNote')}
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              rows={4}
              disabled={processing}
            />
            <div className="flex gap-2">
              <Button onClick={handleAddNote} disabled={processing}>
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('notesTab.adding')}
                  </>
                ) : (
                  t('notesTab.addNote')
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowForm(false);
                  setNewNoteTitle('');
                  setNewNoteContent('');
                }}
                disabled={processing}
              >
                {t('notesTab.cancel')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {notes.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <StickyNote className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>{t('notesTab.noNotes')}</p>
          <p className="text-sm mt-2">{t('notesTab.noNotesHint')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <Card key={note.id}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-muted rounded-lg">
                    <FileText className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    {note.title && (
                      <h4 className="font-semibold text-lg mb-2">{note.title}</h4>
                    )}
                    <p className="text-sm whitespace-pre-wrap mb-3">{note.content}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <div>
                        <span>{safeFormat(new Date(note.created_at), 'MMM d, yyyy HH:mm')}</span>
                        {note.creator && (
                          <span className="ml-2">by {note.creator.display_name}</span>
                        )}
                        {note.contact_id && !note.case_id && (
                          <span className="ml-2 text-amber-600">{t('notesTab.fromContact')}</span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteNote(note.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
